/**
 * TravelAgentPage - Main page for the My Travel Agent feature
 *
 * Full-page two-panel layout:
 *   [Sidebar] | [Chat panel]
 *
 * Artifact chips in messages navigate to the relevant app detail page (same tab, instant via React Router).
 *
 * Manages sessions and messages via travelAgentService.
 * Uses effectiveUserId pattern for Firestore operations (shared trip support).
 * Uses currentUser.uid for Cloud Function calls (auth identity).
 *
 * Session flow:
 * 1. Page loads → subscribe to sessions
 * 2. If no sessions exist → auto-create one
 * 3. User types → save user message → call travelAgent CF → save agent response
 * 4. First message → CF returns title → update session title
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { SmartToy as SmartToyIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTrip, useAuth } from '@trip-planner-monorepo/shared';
import { travelAgentService } from '../../services/travelAgentService.js';
import { agentSuggestionsService } from '../../services/agentSuggestionsService.js';
import { proposalExecutionService } from '../../services/proposalExecutionService.js';
import AgentSidebar from '../AgentSidebar/AgentSidebar.jsx';
import AgentChatPanel from '../AgentChatPanel/AgentChatPanel.jsx';
import './TravelAgentPage.css';

const TravelAgentPage = () => {
  const navigate = useNavigate();
  const { effectiveUserId, selectedTripId, currentTrip, tripOwnerId, isSharedTrip } = useTrip();
  const { currentUser } = useAuth();

  // Session state
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isAgentResponding, setIsAgentResponding] = useState(false);
  const [error, setError] = useState(null);

  // Phase 2: Proposal state
  const [suggestions, setSuggestions] = useState([]);

  // canApprove: true unless viewing a shared trip where the current user is NOT the owner
  const canApprove = !isSharedTrip || (currentUser?.uid === tripOwnerId);

  const debugAgentTrace = true;

  // True once the first Firestore snapshot has arrived
  const sessionsLoadedRef = useRef(false);
  // Guard ref to prevent concurrent auto-creates
  const autoCreatingRef = useRef(false);

  // ---------------------------------------------------------------------------
  // Subscribe to sessions
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!effectiveUserId || !selectedTripId) {
      setSessions([]);
      setCurrentSessionId(null);
      setMessages([]);
      sessionsLoadedRef.current = false;
      autoCreatingRef.current = false;
      return;
    }

    sessionsLoadedRef.current = false;

    const unsubscribe = travelAgentService.subscribeToSessions(
      effectiveUserId,
      selectedTripId,
      (loadedSessions) => {
        setSessions(loadedSessions);

        // On initial load: auto-select an existing active session if one exists.
        // Auto-creation (when there are no sessions) is handled by the separate
        // useEffect below to avoid a race condition where both paths fire concurrently.
        if (!sessionsLoadedRef.current) {
          sessionsLoadedRef.current = true;
          // Include any non-archived live session — 'complete' sessions contain conversation
          // history and should be resumed, not replaced with a new empty session.
          const resumable = loadedSessions.filter((s) => s.status !== 'archived');
          if (resumable.length > 0) {
            // Select the most recently active session
            setCurrentSessionId(resumable[0].id);
          }
          // If no resumable sessions, the second useEffect will create one
        }
      }
    );

    return () => unsubscribe();
  }, [effectiveUserId, selectedTripId]);

  // ---------------------------------------------------------------------------
  // Auto-create a session when there are no active sessions and no session is selected.
  // This handles BOTH:
  //   a) Initial page load with no existing sessions
  //   b) User archives all sessions mid-session
  // The subscription callback only auto-SELECTS; this effect is the sole auto-CREATOR.
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!effectiveUserId || !selectedTripId) return;
    if (!sessionsLoadedRef.current) return;   // wait until initial load
    if (currentSessionId !== null) return;    // already have one
    if (autoCreatingRef.current) return;      // already creating

    const resumable = sessions.filter((s) => s.status !== 'archived');
    if (resumable.length > 0) {
      // There are resumable sessions (active or complete) — just pick the first one
      setCurrentSessionId(resumable[0].id);
      return;
    }

    // No resumable sessions at all — create one automatically
    autoCreatingRef.current = true;
    travelAgentService
      .createSession(effectiveUserId, selectedTripId)
      .then((newSessionId) => {
        setCurrentSessionId(newSessionId);
        autoCreatingRef.current = false;
      })
      .catch((err) => {
        console.error('[TravelAgentPage] Auto-create after archive failed:', err);
        autoCreatingRef.current = false;
      });
  }, [sessions, currentSessionId, effectiveUserId, selectedTripId]);

  // ---------------------------------------------------------------------------
  // Subscribe to messages for the current session
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!effectiveUserId || !selectedTripId || !currentSessionId) {
      setMessages([]);
      return;
    }

    const unsubscribe = travelAgentService.subscribeToMessages(
      effectiveUserId,
      selectedTripId,
      currentSessionId,
      (loadedMessages) => {
        setMessages(loadedMessages);
      }
    );

    return () => unsubscribe();
  }, [effectiveUserId, selectedTripId, currentSessionId]);

  // ---------------------------------------------------------------------------
  // Phase 2: Subscribe to agentSuggestions (all statuses) for this trip
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!effectiveUserId || !selectedTripId) {
      setSuggestions([]);
      return;
    }

    const unsubscribe = agentSuggestionsService.subscribeToSuggestions(
      effectiveUserId,
      selectedTripId,
      {
        onUpdate: (docs) => setSuggestions(docs),
        onError: (err) => console.warn('[TravelAgentPage] Suggestions subscription error:', err)
      }
    );

    return () => unsubscribe();
  }, [effectiveUserId, selectedTripId]);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const handleNewSession = useCallback(async () => {
    if (!effectiveUserId || !selectedTripId) return;

    try {
      const newSessionId = await travelAgentService.createSession(effectiveUserId, selectedTripId);
      setCurrentSessionId(newSessionId);
      setMessages([]);
      setError(null);
    } catch (err) {
      console.error('[TravelAgentPage] Failed to create new session:', err);
      setError('Failed to create a new conversation. Please try again.');
    }
  }, [effectiveUserId, selectedTripId]);

  const handleSelectSession = useCallback((sessionId) => {
    setCurrentSessionId(sessionId);
    setMessages([]);
    setError(null);
  }, []);

  const handleArchiveSession = useCallback(async (sessionId) => {
    if (!effectiveUserId || !selectedTripId) return;

    try {
      await travelAgentService.archiveSession(effectiveUserId, selectedTripId, sessionId);
      // If the archived session is the current one, clear selection
      if (currentSessionId === sessionId) {
        setCurrentSessionId(null);
        setMessages([]);
      }
    } catch (err) {
      console.error('[TravelAgentPage] Failed to archive session:', err);
    }
  }, [effectiveUserId, selectedTripId, currentSessionId]);

  const handleRestoreSession = useCallback(async (sessionId) => {
    if (!effectiveUserId || !selectedTripId) return;
    try {
      await travelAgentService.restoreSession(effectiveUserId, selectedTripId, sessionId);
      setCurrentSessionId(sessionId);
      setError(null);
    } catch (err) {
      console.error('[TravelAgentPage] Failed to restore session:', err);
    }
  }, [effectiveUserId, selectedTripId]);

  const handleDeleteSession = useCallback(async (sessionId) => {
    if (!effectiveUserId || !selectedTripId) return;
    try {
      await travelAgentService.deleteSession(effectiveUserId, selectedTripId, sessionId);
      if (currentSessionId === sessionId) {
        setCurrentSessionId(null);
        setMessages([]);
      }
    } catch (err) {
      console.error('[TravelAgentPage] Failed to delete session:', err);
    }
  }, [effectiveUserId, selectedTripId, currentSessionId]);

  const handleSendMessage = useCallback(async (text) => {
    if (!text.trim() || !effectiveUserId || !selectedTripId || !currentSessionId) return;

    setError(null);

    // Determine if this is the first message (triggers title generation)
    const isFirstMessage = messages.length === 0;

    try {
      // 1. Save user message to Firestore
      await travelAgentService.saveMessage(
        effectiveUserId,
        selectedTripId,
        currentSessionId,
        'user',
        text
      );

      setIsAgentResponding(true);

      // 2. Build conversation history in Gemini format
      const conversationHistory = messages.map((msg) => ({
        role: msg.role === 'user' ? 'user' : 'model',
        parts: [{ text: msg.content }]
      }));

      // 3. Call the Cloud Function
      // For the CF, we pass effectiveUserId as ownerId for Firestore data access
      const result = await travelAgentService.sendToAgent(
        effectiveUserId,
        selectedTripId,
        currentSessionId,
        text,
        conversationHistory,
        isFirstMessage,
        debugAgentTrace
      );

      if (result?.debugTrace) {
        const trace = result.debugTrace;
        console.groupCollapsed(`[TravelAgentTrace] ${trace.traceId || 'no-trace-id'}`);
        console.log('routing', trace.routing || {});
        console.log('memory', trace.memory || {});
        console.log('modelAttempts', trace.modelAttempts || []);
        console.log('secondPass', trace.secondPass || {});
        console.log('prompts', trace.prompts || []);
        console.log('responses', trace.responses || []);
        console.log('final', trace.final || {});
        if (trace.fatalError) {
          console.error('fatalError', trace.fatalError);
        }
        console.groupEnd();
      }

      // 3b–5. Parallelise all post-response Firestore writes:
      //   - Reset session status: 'complete' → 'active' so it auto-selects on next visit
      //   - Save agent message to the messages subcollection
      //   - (Optional) Update session title on the first message
      //   These are independent operations (different fields/subcollections) so they
      //   can safely run concurrently.
      const postResponseWrites = [
        travelAgentService.resetSessionStatus(effectiveUserId, selectedTripId, currentSessionId),
        travelAgentService.saveMessage(
          effectiveUserId,
          selectedTripId,
          currentSessionId,
          'agent',
          result.text || 'I apologize, I could not generate a response.',
          {
            toolsUsed: result.toolsUsed || [],
            artifacts: result.artifacts || []
          }
        )
      ];
      if (isFirstMessage && result.title) {
        postResponseWrites.push(
          travelAgentService.updateSessionTitle(
            effectiveUserId,
            selectedTripId,
            currentSessionId,
            result.title
          )
        );
      }
      await Promise.all(postResponseWrites);
    } catch (err) {
      console.error('[TravelAgentPage] Failed to send message:', err);
      setError('Failed to get a response. Please try again.');
    } finally {
      setIsAgentResponding(false);
    }
  }, [effectiveUserId, selectedTripId, currentSessionId, messages, debugAgentTrace]);

  /**
   * handleReviewMyTrip — triggered by ✨ Review Trip button in TripContextDrawer.
   * Sends "Please review my trip" as a user chat message so the AI responds
   * with the full rich review in the conversation thread.
   */
  const handleReviewMyTrip = useCallback(() => {
    handleSendMessage('Please review my trip');
  }, [handleSendMessage]);

  // ---------------------------------------------------------------------------
  // Phase 2: Proposal handlers
  // ---------------------------------------------------------------------------

  const handleApprove = useCallback(async (suggestionId) => {
    const suggestion = suggestions.find((s) => s.id === suggestionId);
    if (!suggestion) return;
    try {
      await proposalExecutionService.executeApproval({
        effectiveUserId,
        tripId: selectedTripId,
        suggestionId,
        suggestion,
        currentUserId: currentUser?.uid
      });
    } catch (err) {
      console.error('[TravelAgentPage] Approve error:', err);
      setError('Failed to approve proposal. Please try again.');
    }
  }, [effectiveUserId, selectedTripId, suggestions, currentUser]);

  const handleDismiss = useCallback(async (suggestionId) => {
    try {
      await proposalExecutionService.executeDismiss({
        effectiveUserId,
        tripId: selectedTripId,
        suggestionId,
        currentUserId: currentUser?.uid
      });
    } catch (err) {
      console.error('[TravelAgentPage] Dismiss error:', err);
      setError('Failed to dismiss proposal. Please try again.');
    }
  }, [effectiveUserId, selectedTripId, currentUser]);

  const handleModify = useCallback(async (suggestionId, modifiedNote) => {
    try {
      await agentSuggestionsService.updateSuggestionStatus(
        effectiveUserId,
        selectedTripId,
        suggestionId,
        {
          status: 'modified',
          actionedByUserId: currentUser?.uid,
          modifiedData: { userNote: modifiedNote }
        }
      );
    } catch (err) {
      console.error('[TravelAgentPage] Modify error:', err);
      setError('Failed to save modification. Please try again.');
    }
  }, [effectiveUserId, selectedTripId, currentUser]);

  /**
   * handleArtifactClick — navigates directly to the relevant app page (same tab, instant).
   * No panel, no Firestore fetch, no loading state needed.
   *
   * URL map:
   *   ENTITY_VIEW_place         → /trips/{id}/places/{entityId}
   *   ENTITY_VIEW_accommodation → /trips/{id}/accommodation/{entityId}
   *   ENTITY_VIEW_travel        → /trips/{id}/travel/{entityId}
   *   ENTITY_VIEW_day           → /trips/{id}/day/{entityId}
   *   TRIP_TIMELINE             → /trips/{id}  (days list / main trip view)
   */
  const handleArtifactClick = useCallback((chipData) => {
    const { type, id } = chipData;
    const tripBase = `/trips/${selectedTripId}`;

    let path;
    if (type === 'ENTITY_VIEW_place') {
      path = `${tripBase}/places/${id}`;
    } else if (type === 'ENTITY_VIEW_accommodation') {
      path = `${tripBase}/accommodation/${id}`;
    } else if (type === 'ENTITY_VIEW_travel') {
      path = `${tripBase}/travel/${id}`;
    } else if (type === 'ENTITY_VIEW_day') {
      path = `${tripBase}/day/${id}`;
    } else {
      // TRIP_TIMELINE or unknown — navigate to the main trip page (days list)
      path = tripBase;
    }

    navigate(path);
  }, [selectedTripId, navigate]);

  // ---------------------------------------------------------------------------
  // Current session for display
  // ---------------------------------------------------------------------------

  const currentSession = sessions.find((s) => s.id === currentSessionId) || null;

  // ---------------------------------------------------------------------------
  // Render: no trip selected
  // ---------------------------------------------------------------------------

  if (!selectedTripId) {
    return (
      <Box className="travel-agent-page">
        <Box className="travel-agent-no-trip">
          <SmartToyIcon className="travel-agent-no-trip-icon" />
          <Typography variant="h6" color="text.secondary">
            Select a trip to use the Travel Agent
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>
            The Travel Agent helps you plan, research, and organise your trips using AI.
            Select a trip from the header to get started.
          </Typography>
        </Box>
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: main layout
  // ---------------------------------------------------------------------------

  return (
    <Box className="travel-agent-page">
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 10, minWidth: 320 }}
        >
          {error}
        </Alert>
      )}

      <Box className="travel-agent-layout">
        {/* Left: Sessions sidebar */}
        <Box className="travel-agent-sidebar-col">
          <AgentSidebar
            sessions={sessions}
            currentSessionId={currentSessionId}
            onNewSession={handleNewSession}
            onSelectSession={handleSelectSession}
            onArchiveSession={handleArchiveSession}
            onRestoreSession={handleRestoreSession}
            onDeleteSession={handleDeleteSession}
            proposals={suggestions}
            onApprove={handleApprove}
            onDismiss={handleDismiss}
            onModify={handleModify}
            canApprove={canApprove}
          />
        </Box>

        {/* Centre: Chat panel (includes TripContextDrawer at the bottom) */}
        <Box className="travel-agent-chat-col">
          <AgentChatPanel
            currentSession={currentSession}
            messages={messages}
            isAgentResponding={isAgentResponding}
            effectiveUserId={effectiveUserId}
            tripId={selectedTripId}
            currentTrip={currentTrip}
            onSendMessage={handleSendMessage}
            onNewSession={handleNewSession}
            onArtifactClick={handleArtifactClick}
            onReviewTrip={handleReviewMyTrip}
            suggestions={suggestions}
            onApprove={handleApprove}
            onDismiss={handleDismiss}
            onModify={handleModify}
            canApprove={canApprove}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default TravelAgentPage;
